import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import { useToast } from "@/composable/useToast";
import { computed, type Ref } from "vue";
import type { PaginationParams } from "@/types";
import type { UpdateReportTemplatePayload } from "@/types/report-templates.types";

export const useGetReportTemplates = (
  pagination: Ref<PaginationParams>,
  filters: Ref<{ projectId?: number }>,
) => {
  const queryKey = [QueryKey.REPORT_TEMPLATE, pagination, filters];

  return useQuery({
    queryKey,
    queryFn: async () => {
      const res = await APIClient.fetchManyReportTemplates({
        ...filters.value,
        ...pagination.value,
      });

      return res;
    },

    refetchOnWindowFocus: false,
  });
};

export const useGetReportTemplate = (templateId: Ref<number | undefined>) => {
  const enabled = computed(() => !!templateId.value);
  return useQuery({
    queryKey: [QueryKey.REPORT_TEMPLATE, templateId],
    queryFn: async () => {
      if (!templateId.value) return null;
      const res = await APIClient.fetchOneReportTemplate(templateId.value);
      return res;
    },
    enabled,
  });
};

export const useUpdateReportTemplate = (
  templateId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useToast();

  return useMutation({
    mutationFn: async (data: UpdateReportTemplatePayload) => {
      if (!templateId.value) throw new Error("Template ID is undefined");
      return await APIClient.updateReportTemplate(templateId.value, data);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.REPORT_TEMPLATE] });
      showSuccess("toast:report-templates.update-success");
    },
    onError: () => {
      showWarning("toast:report-templates.update-error");
    },
  });
};

export const useDeleteReportTemplate = (
  templateId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useToast();
  return useMutation({
    mutationFn: async () => {
      if (!templateId.value) throw new Error("Template ID is undefined");
      return await APIClient.deleteReportTemplate(templateId.value);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.REPORT_TEMPLATE] });
      showSuccess("toast:report-templates.delete-success");
    },
    onError: () => {
      showWarning("toast:report-templates.delete-error");
    },
  });
};
